/**
 * Bottom Navigation Component
 * 
 * Mobile-friendly bottom navigation for primary actions.
 * Provides touch-friendly tap targets (44x44px minimum).
 * 
 * **Validates: Requirements 10.3, 10.4**
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BottomNavigation as MuiBottomNavigation,
  BottomNavigationAction,
  Paper,
  Badge,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EngineeringIcon from '@mui/icons-material/Engineering';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BuildIcon from '@mui/icons-material/Build';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useIsMobile } from '../../utils/responsive';
import { useTranslation } from 'react-i18next';

interface NavItem {
  labelKey: string;
  value: string;
  icon: React.ReactElement;
  path: string;
  badge?: number;
}

const navigationItems: NavItem[] = [
  {
    labelKey: 'navigation.dashboard',
    value: 'dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
  },
  {
    labelKey: 'navigation.equipment',
    value: 'equipment',
    icon: <EngineeringIcon />,
    path: '/equipment',
  },
  {
    labelKey: 'navigation.inspections',
    value: 'inspections',
    icon: <AssignmentIcon />,
    path: '/inspections',
  },
  {
    labelKey: 'navigation.maintenance',
    value: 'maintenance',
    icon: <BuildIcon />,
    path: '/maintenance',
  },
  {
    labelKey: 'navigation.more',
    value: 'more',
    icon: <MoreHorizIcon />,
    path: '/settings',
  },
];

/**
 * Bottom Navigation Component
 * 
 * Only renders on mobile viewports (< 768px)
 */
export const BottomNavigation: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  // Don't render on desktop
  if (!isMobile) {
    return null;
  }

  // Determine active value based on current path
  const getActiveValue = () => {
    const path = location.pathname;
    const item = navigationItems.find(item => path.startsWith(item.path));
    return item?.value || 'dashboard';
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    const item = navigationItems.find(item => item.value === newValue);
    if (item) {
      navigate(item.path);
    }
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        borderTop: 1,
        borderColor: 'divider',
      }}
      elevation={3}
    >
      <MuiBottomNavigation
        value={getActiveValue()}
        onChange={handleChange}
        showLabels
        sx={{
          height: 64, // Increased height for better touch targets
          '& .MuiBottomNavigationAction-root': {
            minWidth: 44, // Minimum touch target width
            minHeight: 44, // Minimum touch target height
            padding: '8px 12px',
            '&.Mui-selected': {
              color: 'primary.main',
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.75rem',
            '&.Mui-selected': {
              fontSize: '0.75rem',
            },
          },
        }}
      >
        {navigationItems.map((item) => (
          <BottomNavigationAction
            key={item.value}
            label={t(item.labelKey)}
            value={item.value}
            icon={
              item.badge ? (
                <Badge badgeContent={item.badge} color="error">
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )
            }
          />
        ))}
      </MuiBottomNavigation>
    </Paper>
  );
};
